<script setup lang="ts">
import {z} from 'zod'
import type {FormSubmitEvent} from '#ui/types'

const props = defineProps<{ mode: 'signIn' | 'signUp' }>()
const {t} = useI18n()
const localePath = useLocalePath()
const authClient = useAuthClient()

const pending = ref(false)
const authError = ref('')
const state = reactive({name: '', email: '', password: ''})

const schema = computed(function buildSchema() {
  let name = z.string()
  if (props.mode === 'signUp') {
    name = z.string().trim().min(1, t('forms.name.error'))
  }
  return z.object({
    name,
    email: z.email(t('forms.email.error')),
    password: z.string().min(8, t('forms.password.error')),
  })
})

type AuthFormState = { name: string, email: string, password: string }

async function onSubmit(event: FormSubmitEvent<AuthFormState>) {
  pending.value = true
  authError.value = ''

  let failed = false
  if (props.mode === 'signUp') {
    const result = await authClient.signUp.email({
      name: event.data.name,
      email: event.data.email,
      password: event.data.password,
    })
    if (result.error) {
      failed = true
      authError.value = t('platform.auth.signUpError')
    }
  } else {
    const result = await authClient.signIn.email({email: event.data.email, password: event.data.password})
    if (result.error) {
      failed = true
      authError.value = t('platform.auth.error')
    }
  }

  pending.value = false
  if (!failed) {
    await navigateTo(localePath('/stores'))
  }
}
</script>

<template>
  <UForm :schema="schema" :state="state" class="flex flex-col gap-4" @submit="onSubmit">
    <UFormField v-if="mode === 'signUp'" :label="t('platform.auth.nameLabel')" name="name" required>
      <UInput v-model="state.name" autocomplete="name" class="w-full"/>
    </UFormField>
    <UFormField :label="t('platform.auth.emailLabel')" name="email" required>
      <UInput v-model="state.email" type="email" autocomplete="email" class="w-full"/>
    </UFormField>
    <UFormField :label="t('platform.auth.passwordLabel')" name="password" required>
      <UInput v-model="state.password" type="password" autocomplete="current-password" class="w-full"/>
    </UFormField>
    <p v-if="authError" role="alert" class="text-sm text-error">{{ authError }}</p>
    <UButton type="submit" block :loading="pending">
      <template v-if="mode === 'signUp'">{{ t('platform.nav.signUp') }}</template>
      <template v-else>{{ t('platform.nav.login') }}</template>
    </UButton>
  </UForm>
</template>
